import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { listSecureFiles, revokeSecureFile, secureFileErrorCode } from "../../lib/secureFiles/api";
import { formatBytes } from "../../lib/secureFiles/link";
import { intlLocaleFor } from "../../i18n";

const STATUS_STYLE = {
  active: "bg-emerald-500/15 text-emerald-700 border-emerald-200",
  retrieved: "bg-slate-100 text-slate-600 border-slate-200",
  expired: "bg-amber-100 text-amber-700 border-amber-200",
  revoked: "bg-rose-100 text-rose-700 border-rose-200",
};

// Liste minimale : aucun nom (inconnu de SecurePass), aucun lien complet (clé jamais stockée).
export const SecureFilesList = ({ refreshKey }) => {
  const { t, i18n } = useTranslation("files");
  const lng = i18n.resolvedLanguage;
  const fmt = (v) => (v ? new Intl.DateTimeFormat(intlLocaleFor(lng), { dateStyle: "short", timeStyle: "short" }).format(new Date(v)) : "—");
  const [items, setItems] = useState(null);
  const [target, setTarget] = useState(null);

  const load = useCallback(() => listSecureFiles().then(setItems).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const revoke = async () => {
    const id = target;
    setTarget(null);
    try {
      await revokeSecureFile(id);
      toast.success(t("list.revoked"));
    } catch (e) {
      toast.error(t(`errors.${secureFileErrorCode(e)}`, { defaultValue: t("errors.UNKNOWN") }));
    }
    load();
  };

  return (
    <div data-testid="sf-list" className="glass rounded-2xl p-5 sm:p-6">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 className="font-heading text-lg font-semibold text-slate-900">{t("list.title")}</h2>
        <Button data-testid="sf-list-refresh" variant="ghost" size="icon" onClick={load} aria-label={t("list.refresh")}><RefreshCw className="w-4 h-4" /></Button>
      </div>
      <p className="text-xs text-slate-500 mb-4">{t("list.noLinkNote")}</p>
      {items === null && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
      {items?.length === 0 && <p data-testid="sf-list-empty" className="text-sm text-slate-500">{t("list.empty")}</p>}
      <ul className="space-y-2">
        {items?.map((f) => (
          <li key={f.id} data-testid={`sf-item-${f.id}`} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
            <Badge data-testid={`sf-item-status-${f.id}`} variant="outline" className={STATUS_STYLE[f.status]}>{t(`list.statuses.${f.status}`)}</Badge>
            <span className="text-xs text-slate-600">{t("list.createdValue", { v: fmt(f.created_at) })}</span>
            <span className="text-xs text-slate-600">{t("list.sizeValue", { v: formatBytes(f.size, lng) })}</span>
            <span className="text-xs text-slate-600">{t("list.expiresValue", { v: fmt(f.expires_at) })}</span>
            {f.status === "active" && (
              <Button data-testid={`sf-revoke-${f.id}`} size="sm" variant="outline" onClick={() => setTarget(f.id)}
                className="ml-auto border-rose-200 text-rose-700 hover:bg-rose-50"><Ban className="w-3.5 h-3.5 mr-1.5" />{t("list.revoke")}</Button>
            )}
          </li>
        ))}
      </ul>
      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent data-testid="sf-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("list.revoke")}</AlertDialogTitle>
            <AlertDialogDescription>{t("list.revokeConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sf-revoke-cancel">{t("list.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="sf-revoke-confirm" onClick={revoke} className="bg-rose-600 hover:bg-rose-500">{t("list.revoke")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SecureFilesList;
